var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { assertUserTypeOptional } from "healthex-common/checkusertype";
import { UpdateExternalResearcherOrganizationDetailsInput, } from "healthex-common/externalresearcherorganization.dtos";
import { InviteExternalResearcherUserInput, UpdateExternalResearcherUserInput, } from "healthex-common/externalresearcherorganizationuser.dtos";
import { UpdateHealthcareOrganizationDetailsInput } from "healthex-common/healthcareorganization.dtos";
import { InviteHealthcareOrganizationUserInput, UpdateHealthcareOrganizationUserInput, } from "healthex-common/healthcareorganizationuser.dtos";
import { UpdateServiceProviderOrganizationDetailsInput, } from "healthex-common/serviceproviderorganization.dtos";
import { InviteServiceProviderUserInput, UpdateServiceProviderUserInput } from "healthex-common/serviceprovideruser.dtos";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { pick, willNeverHappen } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { NOT_LOADED, ParameterizedRemoteData, RemoteData } from "../Infra/RemoteData";
export class OrganizationsManager {
    constructor(provisioningService, statsService, authManager, invalidationManager, studyService) {
        this.provisioningService = provisioningService;
        this.statsService = statsService;
        this.authManager = authManager;
        this.invalidationManager = invalidationManager;
        this.studyService = studyService;
        this.organizations = new ParameterizedRemoteData(this.invalidationManager, () => [this.authManager.orgId, this.authManager.userType, this.authManager.permissions], () => this.loadOrganizations());
        this.myOrganization = new ParameterizedRemoteData(this.invalidationManager, () => [this.authManager.orgId, this.authManager.userType, this.authManager.permissions], () => this.loadMyOrganization());
        this.myUser = new ParameterizedRemoteData(this.invalidationManager, () => [this.authManager.orgId, this.authManager.userType, this.authManager.permissions], () => this.loadMyUser());
        this.hasAtLeastOneStudy = new ParameterizedRemoteData(this.invalidationManager, () => [this.authManager.orgId], () => this.loadHasAtLeastOneStudy());
        this.organizationUsers = new Map();
    }
    getOrganization(organizationId) {
        var _a;
        const myOrganization = this.myOrganization.value;
        if (myOrganization === NOT_LOADED)
            return NOT_LOADED;
        if (myOrganization === null)
            return null;
        if (organizationId === myOrganization.id) {
            return myOrganization;
        }
        else {
            const otherOrganizations = this.organizations.value;
            if (otherOrganizations === NOT_LOADED)
                return NOT_LOADED;
            if (otherOrganizations === null)
                return null;
            return (_a = otherOrganizations.find((candidate) => candidate.id === organizationId)) !== null && _a !== void 0 ? _a : null;
        }
    }
    async waitForReloadOrganizations() {
        await this.organizations.waitForReload();
        await this.myOrganization.waitForReload();
    }
    async waitForValueOrganizations() {
        await this.organizations.waitForValue();
        await this.myOrganization.waitForValue();
    }
    getUsersForOrganization(organizationId) {
        if (!this.organizationUsers.has(organizationId)) {
            this.organizationUsers.set(organizationId, new RemoteData(this.invalidationManager, async () => {
                const result = await this.provisioningService.getUsersByOrganization(organizationId);
                return result.results;
            }));
        }
        return this.organizationUsers.get(organizationId).value;
    }
    async waitForReloadOrganizationUsers(organizationId) {
        const remoteData = this.organizationUsers.get(organizationId);
        if (remoteData) {
            await remoteData.waitForReload();
        }
    }
    async loadOrganizations() {
        if (!this.authManager.orgId)
            return null;
        if (!this.authManager.permissions.includes(UserPermissions.GLOBAL_PROVISIONING))
            return null;
        const organizations = await this.provisioningService.getOrganizations();
        return organizations.map((organization) => ({
            ...organization,
            users: null,
            features: null,
        }));
    }
    async loadMyOrganization() {
        if (!this.authManager.orgId)
            return null;
        const organization = await this.provisioningService.getOrganizationById(this.authManager.orgId);
        if (!organization)
            return null;
        return {
            ...organization,
            users: this.authManager.permissions.includes(UserPermissions.ORG_ADMIN)
                ? (await this.provisioningService.getUsersByOrganization(this.authManager.orgId)).results
                : [],
        };
    }
    async loadMyUser() {
        if (this.authManager.orgId === null || this.authManager.id === null)
            return null;
        if (this.authManager.userType === UserType.PATIENT) {
            console.log("Skipping user lookup for patient - patients don't have user records");
            return null;
        }
        return await this.loadUserByOrganizationAndId(this.authManager.orgId, this.authManager.id);
    }
    async loadOrganizationPublicProfile(id) {
        return await this.provisioningService.getOrganizationPublicProfileById(id);
    }
    async loadOrganization(id) {
        return await this.provisioningService.getOrganizationById(id);
    }
    async loadHasAtLeastOneStudy() {
        if (this.authManager.orgId === null)
            return null;
        return await this.studyService.hasAtLeastOneStudy(this.authManager.orgId);
    }
    async updateOrganizationDetails(organizationId, update) {
        let updateObject;
        if (update.type === UserType.HEALTHCARE_ORGANIZATION) {
            updateObject = new UpdateHealthcareOrganizationDetailsInput(update);
        }
        else if (update.type === UserType.EXTERNAL_RESEARCHER) {
            updateObject = new UpdateExternalResearcherOrganizationDetailsInput(update);
        }
        else if (update.type === UserType.SERVICE_PROVIDER) {
            updateObject = new UpdateServiceProviderOrganizationDetailsInput(update);
        }
        else {
            willNeverHappen(update);
        }
        return await this.provisioningService.updateOrganizationDetails(organizationId, updateObject);
    }
    async createOrganization(name, type, initialUserInput) {
        const organizationId = await this.provisioningService.createOrganization({ name, type });
        console.log(`Created new organization: ${organizationId}`);
        await this.inviteUser(organizationId, initialUserInput);
        console.log(`Invited first user`);
        return organizationId;
    }
    async editOrganization(organizationId, input) {
        const existing = assertUserTypeOptional(await this.provisioningService.getOrganizationById(organizationId), input.type);
        if (!existing) {
            throw new Error("Editing an object that doesn't exist!");
        }
        if (input.name && input.name !== existing.name) {
            const organizationUpdate = {
                name: input.name,
                type: existing.type,
            };
            await this.provisioningService.updateOrganizationById(organizationId, organizationUpdate);
        }
        if (input.logo instanceof File) {
            const fileUploadInfo = await this.provisioningService.getOrganizationLogoUploadUrl(organizationId, input.logo);
            await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, input.logo);
            input.logoUrl = fileUploadInfo.fileUrl;
        }
        let updateObject;
        if (input.type === UserType.HEALTHCARE_ORGANIZATION) {
            updateObject = new UpdateHealthcareOrganizationDetailsInput(pick(input, "name", "address", "city", "state", "zip", "ssoEnabled", "allowDataAccessForPatientIdentification", "allowDataAccessForDataDelivery", "patientPortalLink", "patientPortalName", "vanitySubdomain", "logoUrl", "setupComplete"));
        }
        else if (input.type === UserType.EXTERNAL_RESEARCHER) {
            updateObject = new UpdateExternalResearcherOrganizationDetailsInput(pick(input, "name", "ssoEnabled", "vanitySubdomain", "logoUrl", "setupComplete", "address", "city", "state", "zip"));
        }
        else if (input.type === UserType.SERVICE_PROVIDER) {
            updateObject = new UpdateServiceProviderOrganizationDetailsInput(pick(input, "ssoEnabled", "vanitySubdomain", "logoUrl", "address", "city", "state", "zip"));
        }
        else {
            willNeverHappen(input);
        }
        await this.provisioningService.updateOrganizationDetails(organizationId, updateObject);
        await this.authManager.refreshTokenInfo();
    }
    async inviteUser(organizationId, input) {
        let inviteObject;
        if (input.type === UserType.HEALTHCARE_ORGANIZATION) {
            inviteObject = new InviteHealthcareOrganizationUserInput(input);
        }
        else if (input.type === UserType.EXTERNAL_RESEARCHER) {
            inviteObject = new InviteExternalResearcherUserInput(input);
        }
        else if (input.type === UserType.SERVICE_PROVIDER) {
            inviteObject = new InviteServiceProviderUserInput(input);
        }
        else {
            willNeverHappen(input);
        }
        const { id: userId } = await this.provisioningService.inviteUser(organizationId, inviteObject);
        await this.waitForReloadOrganizationUsers(organizationId);
        return userId;
    }
    async loadUserByOrganizationAndId(organizationId, userId) {
        return await this.provisioningService.getUserByOrganizationAndId(organizationId, userId);
    }
    async loadUsersByOrganization(organizationId, start, end) {
        return await this.provisioningService.getUsersByOrganization(organizationId, undefined, start, end);
    }
    async editUser(organizationId, userId, input) {
        const existing = await this.provisioningService.getUserByOrganizationAndId(organizationId, userId);
        if (!existing) {
            throw new Error("Editing an object that doesn't exist!");
        }
        if (input.newAvatar) {
            const fileUploadInfo = await this.provisioningService.getUserAvatarUploadUrl(organizationId, userId, input.newAvatar);
            await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, input.newAvatar);
            input.avatarUrl = fileUploadInfo.fileUrl;
        }
        const updateProperties = pick(input, "email", "firstName", "lastName", "title", "phone", "permissions", "avatarUrl", "onboardingComplete", "providerId");
        let updateObject;
        if (input.type === UserType.HEALTHCARE_ORGANIZATION) {
            updateObject = new UpdateHealthcareOrganizationUserInput(updateProperties);
        }
        else if (input.type === UserType.EXTERNAL_RESEARCHER) {
            updateObject = new UpdateExternalResearcherUserInput(updateProperties);
        }
        else if (input.type === UserType.SERVICE_PROVIDER) {
            updateObject = new UpdateServiceProviderUserInput(updateProperties);
        }
        else {
            willNeverHappen(input);
        }
        const result = await this.provisioningService.updateUser(organizationId, userId, updateObject);
        return result;
    }
    async deleteUser(organizationId, userId) {
        await this.provisioningService.deleteUser(organizationId, userId);
        await this.organizations.waitForReload();
        await this.myOrganization.waitForReload();
        await this.waitForReloadOrganizationUsers(organizationId);
    }
    async forcePasswordReset(organizationId, userId) {
        await this.provisioningService.forcePasswordUpdateForUser(organizationId, userId);
        await this.myOrganization.waitForReload();
    }
    get onlyUserIdWithPermission() {
        const orgValue = this.myOrganization.value;
        if (orgValue === NOT_LOADED)
            return NOT_LOADED;
        const users = orgValue === null || orgValue === void 0 ? void 0 : orgValue.users;
        if (!users)
            return null;
        const permissions = [UserPermissions.ORG_ADMIN, UserPermissions.RESEARCH_ADMIN, UserPermissions.RESEARCHER];
        const userIds = {};
        for (const permission of permissions) {
            userIds[permission] = undefined;
        }
        for (const user of users) {
            for (const permission of permissions) {
                if (userIds[permission] === null) {
                    continue;
                }
                if (user.permissions.includes(permission)) {
                    if (userIds[permission]) {
                        userIds[permission] = null;
                        continue;
                    }
                    userIds[permission] = user.id;
                }
            }
        }
        return userIds;
    }
    getCannotBeDeletedReason(user) {
        var _a, _b, _c, _d, _e;
        if (this.authManager.id === user.id) {
            return "SELF";
        }
        if (this.onlyUserIdWithPermission === NOT_LOADED) {
            return NOT_LOADED;
        }
        if (user.id === ((_a = this.onlyUserIdWithPermission) === null || _a === void 0 ? void 0 : _a.ORG_ADMIN) && user.id === ((_b = this.onlyUserIdWithPermission) === null || _b === void 0 ? void 0 : _b.RESEARCH_ADMIN)) {
            return "ONLY_ORG_AND_RESEARCH_ADMIN";
        }
        else if (user.id === ((_c = this.onlyUserIdWithPermission) === null || _c === void 0 ? void 0 : _c.ORG_ADMIN)) {
            return "ONLY_ORG_ADMIN";
        }
        else if (user.id === ((_d = this.onlyUserIdWithPermission) === null || _d === void 0 ? void 0 : _d.RESEARCHER) && this.hasAtLeastOneStudy.value === NOT_LOADED) {
            return NOT_LOADED;
        }
        else if (user.id === ((_e = this.onlyUserIdWithPermission) === null || _e === void 0 ? void 0 : _e.RESEARCHER) && this.hasAtLeastOneStudy.value === true) {
            return "ONLY_RESEARCHER_WITH_AT_LEAST_ONE_STUDY";
        }
        else {
            return null;
        }
    }
}
__decorate([
    observable
], OrganizationsManager.prototype, "myOrganization", void 0);
__decorate([
    observable
], OrganizationsManager.prototype, "myUser", void 0);
__decorate([
    observable
], OrganizationsManager.prototype, "hasAtLeastOneStudy", void 0);
__decorate([
    computed
], OrganizationsManager.prototype, "onlyUserIdWithPermission", null);
